import React from "react";
import { Helmet } from "react-helmet-async";

// Canonical site origin. Keep in sync with public/index.html and the backend
// SITE_URL setting used by the sitemap.
export const SITE_URL = "https://finaiglobal.com";

const DEFAULTS = {
  title: "FinAI — AI portfolio analysis & a council of AI advisors",
  description:
    "A board of AI specialists for your money. Track everything you own, analyze your portfolio, get daily market briefings, and ask anything. Free to start.",
  image: `${SITE_URL}/og-image.png`,
  type: "website",
};

/**
 * Per-page SEO tags. Google renders these (JS); social scrapers fall back to
 * the static tags in public/index.html.
 *
 * Props:
 *  - title        page title (site name is appended unless `bareTitle`)
 *  - description  meta description
 *  - path         pathname for canonical/og:url (e.g. "/blogs"); defaults to current location
 *  - image        absolute og/twitter image URL
 *  - type         og:type ("website" | "article")
 *  - noindex      true => robots noindex,nofollow
 *  - jsonLd       optional structured-data object (rendered as application/ld+json)
 *  - bareTitle    true => use `title` verbatim (don't append " · FinAI")
 */
export default function SEO({
  title,
  description = DEFAULTS.description,
  path,
  image = DEFAULTS.image,
  type = DEFAULTS.type,
  noindex = false,
  jsonLd,
  bareTitle = false,
}) {
  const fullTitle = !title
    ? DEFAULTS.title
    : bareTitle
    ? title
    : `${title} · FinAI`;

  const resolvedPath =
    path ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const url = `${SITE_URL}${resolvedPath}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow"}
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="FinAI Global" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
